import { useEffect, useMemo, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Home, RefreshCw, Copy, Check, ArrowRight, ChevronDown } from "lucide-react";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";

function genErrorId() {
  // err_<12 hex chars>
  const bytes = new Uint8Array(6);
  (typeof crypto !== "undefined" ? crypto : { getRandomValues: (b: Uint8Array) => b.forEach((_, i, a) => (a[i] = Math.floor(Math.random() * 256))) }).getRandomValues(bytes);
  return "err_" + Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function ServerError() {
  const { t } = useTranslation();
  const errorId = useMemo(genErrorId, []);
  const [copied, setCopied] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(t("errors.server.defaultSubject"));
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user?.email) setEmail(data.user.email);
    });
    setBody(`Error ID: ${errorId}\nPath: ${window.location.pathname}\nUser-Agent: ${navigator.userAgent}\n\n`);
  }, [errorId]);

  function copyId() {
    navigator.clipboard?.writeText(errorId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || subject.length < 3) return toast.error(t("errors.server.invalid"));
    setSubmitting(true);
    const { data, error } = await supabase.rpc("submit_support_ticket", {
      _category: "Bug_Report", _priority: "P2", _subject: subject, _body: body, _email: email,
    });
    setSubmitting(false);
    if (error) toast.error(error.message);
    else { toast.success(`${t("errors.server.submitted")} ${data}`); setSubmitted(true); }
  }

  const services: { key: string; status: "operational" | "degraded" }[] = [
    { key: "platform", status: "operational" },
    { key: "api", status: "degraded" },
    { key: "payments", status: "operational" },
    { key: "ai", status: "operational" },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <Seo title={t("errors.server.seoTitle")} />

      {/* Ambient background accents (theme-aware) */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -left-[10%] -top-[20%] h-[600px] w-[600px] rounded-full bg-destructive/20 blur-3xl animate-pulse" />
        <div className="absolute -bottom-[20%] -right-[10%] h-[600px] w-[600px] rounded-full bg-warning/10 blur-3xl animate-pulse" />
      </div>

      <main className="relative z-10 mx-auto max-w-2xl px-6 py-16 text-center md:py-20">
        {/* 500 glyph */}
        <h1 className="mb-9 text-[140px] font-black leading-none tracking-tighter md:text-[200px] bg-gradient-to-br from-destructive to-warning bg-clip-text text-transparent">
          500
        </h1>

        {/* Eyebrow */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-destructive">
          <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
          {t("errors.server.eyebrow")}
        </div>

        {/* Headline */}
        <h2 className="mx-auto mb-5 max-w-[600px] font-display text-3xl font-black leading-[1.15] tracking-tight md:text-4xl">
          {t("errors.server.headline")}
        </h2>

        {/* Sub */}
        <p className="mx-auto mb-8 max-w-[540px] text-base leading-relaxed text-muted-foreground">
          {t("errors.server.sub")}
        </p>

        {/* Error ID chip */}
        <div className="mb-8 inline-flex items-center gap-2.5 rounded-lg border border-border bg-card px-3.5 py-2.5">
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {t("errors.server.errorId")}
          </div>
          <code className="font-mono text-xs font-semibold text-foreground">{errorId}</code>
          <button
            onClick={copyId}
            className={`flex items-center gap-1.5 rounded p-1 font-mono text-[11px] font-semibold transition-colors ${
              copied ? "text-success" : "text-muted-foreground hover:text-foreground"
            }`}
            aria-label={t("errors.server.copyId")}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied && <span>{t("errors.server.idCopied")}</span>}
          </button>
        </div>

        {/* CTAs */}
        <div className="mb-14 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:gap-3">
          <Button size="lg" onClick={() => window.location.reload()} className="gap-2">
            <RefreshCw size={16} /> {t("errors.server.retry")}
          </Button>
          <Button size="lg" variant="outline" asChild className="gap-2">
            <Link to="/"><Home size={16} /> {t("errors.server.home")}</Link>
          </Button>
        </div>

        {/* Status card */}
        <div className="mb-6 rounded-2xl border border-border bg-card p-6 text-left">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-foreground">{t("errors.server.statusH")}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("errors.server.statusSub")}</p>
            </div>
            <a
              href={`https://${t("errors.server.statusLink")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-primary hover:underline"
            >
              {t("errors.server.statusLink")} <ArrowRight size={12} />
            </a>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {services.map((s) => {
              const isOk = s.status === "operational";
              return (
                <div key={s.key} className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 rounded-full ${isOk ? "bg-success" : "bg-warning"}`} />
                    <div className="text-[11px] font-semibold text-muted-foreground">
                      {t(`errors.server.statusServices.${s.key}`)}
                    </div>
                  </div>
                  <div
                    className={`font-mono text-[11px] font-bold uppercase tracking-wider ${
                      isOk ? "text-success" : "text-warning"
                    }`}
                  >
                    {isOk ? t("errors.server.statusOperational") : t("errors.server.statusDegraded")}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contact link */}
        <div className="mb-6 text-sm text-muted-foreground">
          {t("errors.server.contact")}{" "}
          <Link to="/contact" className="font-semibold text-primary hover:underline">
            {t("errors.server.contactLink")} →
          </Link>
        </div>

        {/* Optional report disclosure (preserves submit_support_ticket RPC) */}
        <Collapsible open={reportOpen} onOpenChange={setReportOpen} className="text-left">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="mx-auto gap-1.5 text-xs text-muted-foreground">
              <ChevronDown size={14} className={`transition-transform ${reportOpen ? "rotate-180" : ""}`} />
              {reportOpen ? t("errors.server.reportHide") : t("errors.server.reportToggle")}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            {submitted ? (
              <div className="rounded-xl border border-success/30 bg-success/10 p-5 text-center">
                <h4 className="text-base font-bold text-foreground">{t("errors.server.thanks")}</h4>
                <p className="mt-1 text-sm text-muted-foreground">{t("errors.server.thanksDesc")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-xl border border-border bg-card p-5">
                <div className="grid gap-3">
                  <div>
                    <Label className="text-xs">{t("errors.server.email")}</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("errors.server.subject")}</Label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} required className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("errors.server.details")}</Label>
                    <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="mt-1 font-mono text-xs" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-fit gap-2">
                    {submitting && <Loader2 className="animate-spin" size={14} />}
                    {t("errors.server.submit")}
                  </Button>
                </div>
              </form>
            )}
          </CollapsibleContent>
        </Collapsible>
      </main>
    </div>
  );
}